import { extractPlantId } from "../utils/Utils";
import { CIP_SOURCE_OPTIONS } from "../constants/global.constants";
import {
  deleteRequest,
  getRequest,
  getRequestWithAuthorization,
  postRequest,
  putRequest,
} from "./api";
import { API_URLS } from "./appUrls";
import { store } from "./networkUtil";

const getBaseURL = () => `${API_URLS.plants}/${extractPlantId()}`;

export const getToken = (header, i18n) =>
  getRequestWithAuthorization(`${API_URLS.token}`, header, i18n);

export const accessRequest = (payload) =>
  postRequest(`${API_URLS.user}/${API_URLS.accessRequest}`, payload);

export const acceptTC = () => postRequest(`${API_URLS.acceptTC}`);

export const uploadSelectedFile = (
  fileData,
  chunkIndex,
  totalChunks,
  selectedTrain,
  commitBlockList,
) =>
  postRequest(
    `${getBaseURL()}/uploadfile?chunkIndex=${chunkIndex}&totalChunks=${totalChunks}&train_num=${selectedTrain}&commit_block_list=${commitBlockList}`,
    fileData,
  );
export const uploadSelectedSheet = (selectedTrain, fileName, sheetName) =>
  postRequest(
    `${getBaseURL()}/sheet?train_num=${selectedTrain}&file_name=${fileName}&sheet_name=${encodeURIComponent(sheetName)}`,
  );

export const getLastFileDetails = (selectedTrain) =>
  getRequest(`${getBaseURL()}/${API_URLS.details}?train_num=${selectedTrain}`);

export const updatePlantInformation = (payload, selectedTrain) =>
  putRequest(
    `${getBaseURL()}/${API_URLS.plantDetails}?train_num=${selectedTrain}`,
    payload,
  );

export const getCIPs = (
  selectedTrain,
  pageSize = 10,
  currentPage = 1,
  source = null,
  historicOnly = false,
) => {
  const appName = store.getState().app;
  let baseURL = getBaseURL();
  if (appName !== "ca") {
    baseURL = `ca/${baseURL}`;
  }

  const formattedSource =
    source === CIP_SOURCE_OPTIONS.USER
      ? Object.values(CIP_SOURCE_OPTIONS)
      : [source];

  const sourceParams = new URLSearchParams();

  formattedSource.forEach((item) => {
    sourceParams.append("source", item);
  });

  const limit = pageSize;
  const offset = (currentPage - 1) * pageSize;

  let url = `${baseURL}/${API_URLS.cips}?train_num=${selectedTrain}&historic_only=${historicOnly}`;
  if (limit && offset) {
    url += `&limit=${limit}&offset=${offset}`;
  }
  if (source) {
    url += `&${sourceParams.toString()}`;
  }

  return getRequest(url);
};

export const uploadBulkCIPs = (selectedTrain, payload) => {
  return postRequest(
    `${getBaseURL()}/${API_URLS.cips}/${
      API_URLS.bulk
    }?train_num=${selectedTrain}`,
    payload,
  );
};

export const uploadCips = (payload, selectedTrain) => {
  const appName = store.getState().app;
  let baseURL = getBaseURL();
  if (appName !== "ca") {
    baseURL = `ca/${baseURL}`;
  }

  return postRequest(
    `${baseURL}/${API_URLS.uploadCips}?train_num=${selectedTrain}`,
    payload,
  );
};

export const uploadRecurrentCips = (
  payload,
  selectedTrain,
  proceedMissingFlag,
) => {
  let baseURL = getBaseURL();

  return postRequest(
    `${baseURL}/${API_URLS.uploadCips}/${API_URLS.recurrent}?train_num=${selectedTrain}&proceed_on_missing=${proceedMissingFlag}`,
    payload,
  );
};

export const editCip = (cipId, payload, selectedTrain) => {
  const appName = store.getState().app;
  let baseURL = getBaseURL();
  if (appName !== "ca") {
    baseURL = `ca/${baseURL}`;
  }
  return putRequest(
    `${baseURL}/${API_URLS.uploadCips}/${cipId}?train_num=${selectedTrain}`,
    payload,
  );
};

export const deleteCipsBySource = (selectedTrain, source) => {
  const url = `${getBaseURL()}/${
    API_URLS.cips
  }?train_num=${selectedTrain}&cip_source=${source}`;
  return deleteRequest(url);
};

export const deleteCip = (cipId, selectedTrain) => {
  const appName = store.getState().app;
  let baseURL = getBaseURL();
  if (appName !== "ca") {
    baseURL = `ca/${baseURL}`;
  }

  return deleteRequest(
    `${baseURL}/${API_URLS.uploadCips}/${cipId}?train_num=${selectedTrain}`,
  );
};

export const cleaningEffectivness = (selectedTrain) =>
  getRequest(
    `${getBaseURL()}/${
      API_URLS.executeCleaning
    }?recalculate=true&train_num=${selectedTrain}`,
  );

export const cleaningEffectivnessStatus = (task_id, selectedTrain) =>
  getRequest(
    `${getBaseURL()}/${
      API_URLS.executeCleaningStatus
    }?run_id=${task_id}&train_num=${selectedTrain}`,
  );

export const cleanedData = (selectedTrain) =>
  getRequest(
    `${getBaseURL()}/${API_URLS.cleanedData}?train_num=${selectedTrain}`,
  );

export const rerunCIPs = (selectedTrain) =>
  getRequest(
    `${getBaseURL()}/${API_URLS.cips}/${
      API_URLS.rerun
    }?train_num=${selectedTrain}`,
  );

export const getFoulingInformation = (selectedTrain) =>
  getRequest(`${getBaseURL()}/${API_URLS.fouling}?train_num=${selectedTrain}`);

export const getPredictLimits = (selectedTrain) => {
  const appName = store.getState().app;
  let baseURL = getBaseURL();
  if (appName !== "ca") {
    baseURL = `ca/${baseURL}`;
  }
  return getRequest(`${baseURL}/limits?train_num=${selectedTrain}`);
};

export const predictionCalculation = (
  window,
  selectedTrain,
  proceedMissingFlag = false,
) =>
  getRequest(
    `${getBaseURL()}/prediction/calculation?forecast_window=${window}&train_num=${selectedTrain}&proceed_on_missing_cips=${proceedMissingFlag}`,
  );

export const getPredictionData = (selectedTrain, forecastWindow) =>
  getRequest(
    `${getBaseURL()}/prediction?train_num=${selectedTrain}&forecast_window=${forecastWindow}`,
  );

export const getPredictionStatus = (task_id) =>
  getRequest(`${getBaseURL()}/prediction/status?run_id=${task_id}`);

export const getHeaders = () =>
  getRequest(`${getBaseURL()}/${API_URLS.header}`);

export const getfoulingType = () =>
  getRequest(`${getBaseURL()}/${API_URLS.foulingType}`);

export const getAnalysisJob = (forecast_window, selectedTrain) =>
  getRequest(
    `${getBaseURL()}/${
      API_URLS.analysisjob
    }?forecast_window=${forecast_window}&train_num=${selectedTrain}`,
  );

export const getAnalysisStatus = (task_id) =>
  getRequest(`${getBaseURL()}/${API_URLS.analysisStatus}?run_id=${task_id}`);

export const getAnalysis = (selectedTrain, forecastWindow) =>
  getRequest(
    `${getBaseURL()}/${
      API_URLS.analysis
    }?train_num=${selectedTrain}&forecast_window=${forecastWindow}`,
  );

export const getType = () => getRequest(`${getBaseURL()}/${API_URLS.type}`);

export const getPlants = () => getRequest(`${API_URLS.plants}`);

export const getPlantDetails = (plantId, includeFile = false, selectedTrain) =>
  getRequest(
    `${API_URLS.plants}/${plantId}?includeFile=${includeFile}&sel_train=${selectedTrain}`,
  );

export const deletePlants = () => deleteRequest(`${getBaseURL()}`);

export const savePlant = (payload) =>
  postRequest(`${API_URLS.plants}`, payload);

export const sharePlant = (plant_id, payload) =>
  postRequest(`${API_URLS.plants}/${plant_id}/${API_URLS.share}`, payload);

export const unsharePlant = (plant_id, email) =>
  deleteRequest(
    `${API_URLS.plants}/${plant_id}/${API_URLS.share}?shared_email=${email}`,
  );

export const getUsers = (plant_id) =>
  getRequest(`${API_URLS.plants}/${plant_id}/${API_URLS.users}`);

export const editPlant = (plantId, payload) =>
  putRequest(`${API_URLS.plants}/${plantId}`, payload);

export const getSheetColumns = (fileId, selectedTrain) =>
  getRequest(
    `${getBaseURL()}/${API_URLS.cols}/${fileId}?train_num=${selectedTrain}`,
  );

export const getColumnMapping = (selectedTrain) =>
  getRequest(
    `${getBaseURL()}/${API_URLS.colmapping}?train_num=${selectedTrain}`,
  );

export const updateColumnMapping = (fileId, payload, selectedTrain) =>
  putRequest(
    `${getBaseURL()}/${
      API_URLS.colmapping
    }/${fileId}?train_num=${selectedTrain}`,
    payload,
  );

export const getReference = (selectedTrain) =>
  getRequest(
    `${getBaseURL()}/${API_URLS.refcondition}?train_num=${selectedTrain}`,
  );

export const confirmRefrenceCondition = (fileId, payload, selectedTrain) =>
  putRequest(
    `${getBaseURL()}/${
      API_URLS.refcondition
    }/${fileId}?train_num=${selectedTrain}`,
    payload,
  );

export const getDataVisualization = (fileId, selectedTrain, appendId) => {
  let url = `${getBaseURL()}/files/${fileId}/${
    API_URLS.visualization
  }?train_num=${selectedTrain}`;
  if (appendId) {
    url += `&append_id=${appendId}`;
  }
  return getRequest(url);
};

export const setadvanceSettings = (payload, selectedTrain) =>
  putRequest(
    `${getBaseURL()}/${API_URLS.advancesettings}?train_num=${selectedTrain}`,
    payload,
  );

export const getadvanceSettings = (selectedTrain) =>
  getRequest(
    `${getBaseURL()}/${API_URLS.advancesettings}?train_num=${selectedTrain}`,
  );

export const getPlantReadiness = () =>
  getRequest(`${getBaseURL()}/${API_URLS.readiness}`);

export const getProceedToAnalysis = (fileId, selectedTrain) =>
  getRequest(
    `${getBaseURL()}/files/${fileId}/${
      API_URLS.proceedToAnalysis
    }?train_num=${selectedTrain}`,
  );

export const setState = (state, selectedTrain) =>
  putRequest(
    `${getBaseURL()}/${
      API_URLS.state
    }?state=${state}&train_num=${selectedTrain}`,
  );

export const saveUser = (payload) => postRequest(`${API_URLS.user}`, payload);

export const getAccessRequest = (
  page = 1,
  pageSize = 5,
  filters,
  sortField,
  sortOrder,
) => {
  const offset = (page - 1) * pageSize;
  const params = new URLSearchParams({
    sort_by: sortField,
    order: sortOrder,
    limit: pageSize,
    offset,
  });

  if (filters && filters.length > 0) {
    filters.forEach((f) => params.append("filters", f));
  }

  const url = `${API_URLS.admin}/${
    API_URLS.accessRequests
  }?${params.toString()}`;
  return getRequest(url);
};

export const updateRequests = (payload) =>
  putRequest(`${API_URLS.admin}/${API_URLS.accessRequests}`, payload);

export const getAllUsers = (
  page = 1,
  pageSize = 5,
  filters,
  sortField,
  sortOrder,
) => {
  const offset = (page - 1) * pageSize;
  const params = new URLSearchParams({
    sort_by: sortField,
    order: sortOrder,
    limit: pageSize,
    offset,
  });

  if (filters && filters.length > 0) {
    filters.forEach((f) => params.append("filters", f));
  }

  const url = `${API_URLS.admin}/${API_URLS.users}?${params.toString()}`;
  return getRequest(url);
};

export const getAllPlantsForUser = (userEmail) => {
  const url = `${API_URLS.admin}/user-plants?user_email=${encodeURIComponent(
    userEmail,
  )}`;
  return getRequest(url);
};

export const exportExcel = (type) => {
  const url = `${API_URLS.admin}/export-excel?type=${encodeURIComponent(type)}`;
  // Request as blob for Excel download
  return getRequest(url, { responseType: "blob" });
};

export const downloadDatasetFile = (selectedTrain) => {
  const url = `${getBaseURL()}/${
    API_URLS.downloadFile
  }/file?train_num=${selectedTrain}`;
  // Request as blob for file download
  return getRequest(url, { responseType: "blob" });
};

export const getDefaultCIPValues = (selectedTrain) =>
  getRequest(`${getBaseURL()}/cips/defaults?train_num=${selectedTrain}`);

export const editDefaultCIPValues = (selectedTrain, data) =>
  putRequest(`${getBaseURL()}/cips/defaults?train_num=${selectedTrain}`, data);

export const uploadAppendFile = (
  fileData,
  chunkIndex,
  totalChunks,
  selectedTrain,
  commitBlockList,
  appendId = null,
) => {
  let url = `${getBaseURL()}/${API_URLS.upload}/${
    API_URLS.appendFile
  }?train_num=${selectedTrain}&chunkIndex=${chunkIndex}&totalChunks=${totalChunks}&commit_block_list=${commitBlockList}`;
  if (appendId) {
    url += `&append_id=${appendId}`;
  }
  return postRequest(url, fileData);
};

export const appendExistingFile = (
  selectedTrain,
  appendId,
  proceed_on_warning = false,
) =>
  postRequest(
    `${getBaseURL()}/${
      API_URLS.appendFile
    }?train_num=${selectedTrain}&append_id=${appendId}&proceed_on_warning=${proceed_on_warning}`,
  );

export const deleteAppendFile = (selectedTrain, appendId) =>
  deleteRequest(
    `${getBaseURL()}/${
      API_URLS.appendFile
    }?train_num=${selectedTrain}&append_id=${appendId}`,
  );

export const getWordReport = (trainNum) => {
  const url = `${getBaseURL()}/${API_URLS.report}?train_num=${trainNum}`;
  return getRequest(url, { responseType: "blob" });
};
export const getLifetimeWordReport = (trainNum, payload = {}) => {
  const url = `${getBaseURL()}/${API_URLS.report}?train_num=${trainNum}`;
  return postRequest(url, payload, { responseType: "blob" });
};

export const getRONFElements = (waterType) => {
  let url = `/${API_URLS.roElements}`;
  if (waterType) {
    url += `?feed_water_type=${waterType}`;
  }
  return getRequest(url);
};

export const getPlantsAvailableForBenchmark = () => {
  return getRequest(
    `${API_URLS.plants}/${API_URLS.benchmarking}/${API_URLS.availability}`,
  );
};

export const getBenchmarkingData = (payload) => {
  return postRequest(
    `${API_URLS.plants}/${API_URLS.benchmarking}/${API_URLS.data}`,
    payload,
  );
};
